import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Send, Filter, CreditCard, Lock, Unlock, CalendarClock, Search } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import MessageFiles from '../../components/MessageFiles';
import LessonFiles from '../../components/LessonFiles';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import PasswordInput from '../../components/PasswordInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function TeachersTab({ ctx }) {
  const {
    token,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  } = ctx;
  return (
          <TabsContent value="teachers" data-testid="teachers-content">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Управление на учители</h2>
                <Dialog open={createTeacherDialogOpen} onOpenChange={setCreateTeacherDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={handleOpenCreateTeacher}
                      data-testid="add-teacher-btn"
                      className="bg-[#00966E] hover:bg-[#007a59] text-white flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Добави учител
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Добавяне на учител</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="teacher-first-name">Име</Label>
                          <Input
                            id="teacher-first-name"
                            value={newUser.first_name}
                            onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                            placeholder="Мария"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="teacher-last-name">Фамилия</Label>
                          <Input
                            id="teacher-last-name"
                            value={newUser.last_name}
                            onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                            placeholder="Иванова"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="username">Потребителско име (за вход)</Label>
                        <Input
                          id="username"
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="password">Парола</Label>
                        <PasswordInput
                          id="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Имейл</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Класове (можете да изберете повече от един)</Label>
                        <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-3">
                          {classes.map((cls) => (
                            <div key={cls.class_id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`teacher-class-${cls.class_id}`}
                                checked={newUser.class_ids.includes(cls.class_id)}
                                onCheckedChange={() => toggleClassSelection(cls.class_id, false)}
                              />
                              <label
                                htmlFor={`teacher-class-${cls.class_id}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {cls.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Button type="submit" className="w-full bg-[#00966E] hover:bg-[#007a59] text-white">
                        Добави
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  data-testid="teacher-search-input"
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  placeholder="Търсене по име, потребител, имейл или клас..."
                  className="pl-9"
                />
                {teacherSearch && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                    {filteredTeachers.length} от {teachers.length}
                  </span>
                )}
              </div>

              <div className="rounded-md border border-slate-200 overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Потребител</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Имейл</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Класове</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-500">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeachers.map((teacher) => (
                      <tr key={teacher.user_id} data-testid="teacher-row" className={`border-t border-slate-100 hover:bg-slate-50/50 ${teacher.is_locked ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-slate-900">{getFullName(teacher)}</div>
                            {teacher.is_locked && (
                              <Badge className="bg-red-100 text-red-700 text-xs flex items-center gap-1">
                                <Lock className="h-3 w-3" /> Заключен
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-400">@{teacher.username}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{teacher.email || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {teacher.class_ids.map(cid => {
                            const cls = classes.find(c => c.class_id === cid);
                            return cls?.name || null;
                          }).filter(Boolean).join(', ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-2">
                            {teacher.is_locked && (
                              <Button
                                onClick={() => handleUnlockUser(teacher.user_id, teacher.username)}
                                variant="ghost"
                                size="sm"
                                data-testid="unlock-teacher-btn"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Отключи акаунта"
                              >
                                <Unlock className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              onClick={() => handleEditUser(teacher)}
                              variant="ghost"
                              size="sm"
                              data-testid="edit-teacher-btn"
                              className="text-[#00966E] hover:text-[#007a59] hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteUser(teacher.user_id)}
                              variant="ghost"
                              size="sm"
                              data-testid="delete-teacher-btn"
                              className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
  );
}
